import { Bot, Plug, Search, Globe, Clipboard, Package, Brain } from "../../../icons"
import type { ComponentType, ReactNode } from 'react'
import { DataTable, type DataTableColumn } from '../../data-table'

interface Props {
 adoption: Record<string, { sessions: number; pct: number }>
 totalSessions: number
 /** Integrated card title — forwarded to DataTable. */
 title?: ReactNode
}

type IconType = ComponentType<{ className?: string; strokeWidth?: number }>

interface FeatureRow {
 key: string
 sessions: number
 pct: number
 label: string
 Icon: IconType
}

const FEATURE_LABELS: Record<string, { label: string; Icon: IconType }> = {
 task_agents:       { label: 'Task Agents',       Icon: Bot       },
 mcp:               { label: 'MCP Servers',       Icon: Plug      },
 web_search:        { label: 'Web Search',        Icon: Search    },
 web_fetch:         { label: 'Web Fetch',         Icon: Globe     },
 plan_mode:         { label: 'Plan Mode',         Icon: Clipboard },
 git_commits:       { label: 'Git Commits',       Icon: Package   },
 extended_thinking: { label: 'Extended Thinking', Icon: Brain     },
}

const columns: DataTableColumn<FeatureRow>[] = [
 {
  key: 'feature',
  header: 'Feature',
  sort: (a, b) => a.label.localeCompare(b.label),
  render: (r) => {
   const Icon = r.Icon
   return (
    <span className="inline-flex items-center gap-2 text-foreground">
     <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
     {r.label}
    </span>
   )
  },
 },
 {
  key: 'sessions',
  header: 'Sessions',
  sort: (a, b) => a.sessions - b.sessions,
  defaultDesc: true,
  render: (r) => r.sessions.toLocaleString(),
 },
 {
  key: 'pct',
  header: '% Of Total',
  sort: (a, b) => a.pct - b.pct,
  defaultDesc: true,
  render: (r) => `${(r.pct * 100).toFixed(1)}%`,
 },
 {
  key: 'adoption',
  header: 'Adoption',
  sortable: false,
  headerClassName: 'w-32',
  render: (r) => (
   <div className="h-2 bg-muted rounded-lg overflow-hidden w-24">
    <div
     className="h-full rounded-lg bg-[var(--chart-4)]/60"
     style={{ width: `${Math.round(r.pct * 100)}%` }}
    />
   </div>
  ),
 },
]

export function FeatureAdoptionTable({ adoption, totalSessions, title }: Props) {
 const rows: FeatureRow[] = Object.entries(adoption)
  .map(([key, data]) => {
   const meta = FEATURE_LABELS[key]
   if (!meta) return null
   return { key, sessions: data.sessions, pct: data.pct, label: meta.label, Icon: meta.Icon }
  })
  .filter((r): r is FeatureRow => r !== null)

 return (
  <DataTable
   title={title}
   columns={columns}
   rows={rows}
   rowKey={(r) => r.key}
   initialSort={{ key: 'sessions', dir: 'desc' }}
   empty="No feature data"
   footer={`${totalSessions.toLocaleString()} total sessions analyzed`}
  />
 )
}
